import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  manifestModifiedSinceSubmit,
  summarizeManifest,
  type ManifestExportFormat,
  type PassengerDto,
  type TripDto,
  type TripPassengerDto,
} from "@tc/shared";
import {
  Avatar,
  Button,
  Card,
  DocChip,
  Icon,
  ProgressBar,
  Segmented,
  TextInput,
  Toast,
} from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { anchorNewTab, openFileTab } from "@/lib/download";
import { useFormatters } from "@/lib/i18n/format";
import { READINESS } from "@/features/roster/labels";
import { rosterApi } from "@/features/roster/api";
import { manifestApi, runManifestExport } from "./api";

type RoleTab = "all" | "player" | "staff" | "assigned";

/**
 * Manifest tab — the shared passenger-list builder (TW-3448), rendered on a trip's Passengers tab
 * for both portals. Players + staff come from the club's persistent roster (TW-3447); ticking a row
 * assigns/unassigns them on the trip (the `trip_passenger` join), and the bar shows live progress
 * toward the requested pax, the player/staff split, and document-readiness warnings. The manifest
 * is co-edited — both the club and TC build/edit it — and only the club gets the "submit to TC"
 * action (the server gates it to club users and notifies TC). An assignment made on one side is
 * visible to the other through the same RLS-scoped store.
 *
 * `locked` (a finished/Completed trip) renders the list as a read-only record: no assigning, no
 * submit, and only the passengers who were on the list — just the closed-out roster + exports.
 */
export function ManifestTab({ trip, locked = false }: { trip: TripDto; locked?: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { isBackOffice } = useAuthz();

  const [roster, setRoster] = useState<PassengerDto[] | null>(null);
  const [manifest, setManifest] = useState<TripPassengerDto[]>([]);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState<ManifestExportFormat | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // When the club last submitted the list — drives the submission-status line under the button.
  const [submittedAt, setSubmittedAt] = useState<string | null>(trip.manifestSubmittedAt);
  // Whether the list has been edited since that submission (server-seeded, then tracked locally).
  const [dirty, setDirty] = useState(
    manifestModifiedSinceSubmit(trip.manifestSubmittedAt, trip.manifestUpdatedAt),
  );

  const [tab, setTab] = useState<RoleTab>("all");
  const [q, setQ] = useState("");

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }, []);

  const load = useCallback(async () => {
    setError(false);
    try {
      // The roster + the current manifest. RLS scopes a club user to its own; back office passes
      // the trip's club id (the controller ignores the filter for club users, so it is safe for both).
      const [r, m] = await Promise.all([rosterApi.list(trip.clubId), manifestApi.list(trip.id)]);
      setRoster(r);
      setManifest(m);
    } catch {
      setError(true);
    }
  }, [trip.id, trip.clubId]);

  useEffect(() => {
    void load();
  }, [load]);

  const assignedIds = useMemo(() => new Set(manifest.map((m) => m.passengerId)), [manifest]);
  const summary = useMemo(
    () =>
      summarizeManifest(roster ?? [], assignedIds, trip.paxCount, {
        players: trip.playerCount,
        staff: trip.staffCount,
      }),
    [roster, assignedIds, trip.paxCount, trip.playerCount, trip.staffCount],
  );

  /** Run a manifest mutation, swap in the returned list, and surface a failure as a toast. */
  const mutate = useCallback(
    async (op: () => Promise<TripPassengerDto[]>) => {
      setBusy(true);
      try {
        setManifest(await op());
        // Editing the list after it was submitted leaves it out of sync with what TC last saw.
        if (submittedAt) setDirty(true);
      } catch {
        flash(t("manifest.saveError"));
      } finally {
        setBusy(false);
      }
    },
    [flash, t, submittedAt],
  );

  /** Whether assigning this not-yet-assigned passenger would exceed the trip's requested count. */
  const atCapacity = (p: PassengerDto) =>
    summary.hasRoleTargets &&
    !assignedIds.has(p.id) &&
    (p.isStaff ? summary.staffComplete : summary.playersComplete);

  const toggle = (p: PassengerDto) => {
    if (assignedIds.has(p.id)) return mutate(() => manifestApi.unassign(trip.id, p.id));
    // Don't let the manifest exceed the players/staff the request asked for.
    if (atCapacity(p)) {
      flash(t(p.isStaff ? "manifest.staffFull" : "manifest.playersFull"));
      return;
    }
    return mutate(() => manifestApi.assign(trip.id, { passengerId: p.id }));
  };
  const clearAll = () =>
    mutate(async () => {
      let latest = manifest;
      for (const m of manifest) latest = await manifestApi.unassign(trip.id, m.passengerId);
      return latest;
    });

  async function submit() {
    setSubmitting(true);
    try {
      const res = await manifestApi.submit(trip.id);
      setSubmittedAt(res.submittedAt);
      setDirty(false);
      flash(t("manifest.submitted"));
    } catch {
      flash(t("manifest.submitError"));
    } finally {
      setSubmitting(false);
    }
  }

  /** Generate the manifest in the worker, then open the signed URL in the tab pre-opened at click
   *  time (UAT round 3 — a same-tab anchor navigated the app to the file; see lib/download.ts). */
  async function exportManifest(format: ManifestExportFormat) {
    const tab = openFileTab(); // synchronously, inside the click
    setExporting(format);
    flash(t("manifest.export.generating"));
    try {
      const { url } = await runManifestExport(trip.id, format);
      if (tab) tab.deliver(url);
      else anchorNewTab(url);
    } catch {
      tab?.fail();
      flash(t("manifest.export.error"));
    } finally {
      setExporting(null);
    }
  }

  if (error) {
    return (
      <Card>
        <div className="empty">{t("manifest.loadError")}</div>
      </Card>
    );
  }
  if (!roster) {
    return (
      <Card>
        <div className="empty">{t("common.loading")}</div>
      </Card>
    );
  }
  if (roster.length === 0) {
    return (
      <Card>
        <div className="empty">
          <div className="empty-ic">
            <Icon name="roster" size={24} />
          </div>
          {t("manifest.noRoster")}
        </div>
      </Card>
    );
  }

  const needle = q.trim().toLowerCase();
  const rows = roster.filter((p) => {
    // Locked = the closed-out record: only the players/staff who were on the list, nothing else.
    if (locked && !assignedIds.has(p.id)) return false;
    if (tab === "player" && p.isStaff) return false;
    if (tab === "staff" && !p.isStaff) return false;
    if (tab === "assigned" && !assignedIds.has(p.id)) return false;
    if (needle && !p.fullName.toLowerCase().includes(needle)) return false;
    return true;
  });

  /** CSV/PDF export buttons — read-only, so they stay available even on a locked list. */
  const exportButtons = (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon="download"
        onClick={() => exportManifest("csv")}
        disabled={summary.assignedCount === 0 || exporting !== null}
      >
        {t("manifest.csv")}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        icon="download"
        onClick={() => exportManifest("pdf")}
        disabled={summary.assignedCount === 0 || exporting !== null}
      >
        {t("manifest.pdf")}
      </Button>
    </>
  );

  /** One stacked progress row for a role (players or staff), with its own Complete + docs badges. */
  const roleRow = (role: {
    key: string;
    labelKey: string;
    assigned: number;
    requested: number;
    pct: number;
    complete: boolean;
    docIssues: number;
  }) => (
    <div className="mf-role" key={role.key}>
      <span className="mf-sub">
        <span>
          <b>{role.assigned}</b>/{role.requested} {t(role.labelKey)}
        </span>
      </span>
      <ProgressBar value={role.pct} variant={role.complete ? "success" : "primary"} />
      {role.complete && (
        <DocChip status="ok" icon="check" iconSize={12} label={t("manifest.complete")} />
      )}
      {/* Docs badge only when this role has someone assigned (nothing to be "ready" otherwise). */}
      {role.assigned > 0 &&
        (role.docIssues > 0 ? (
          <DocChip
            status="warn"
            icon="alert"
            iconSize={12}
            label={`${role.docIssues} ${t("manifest.docsAttention")}`}
          />
        ) : (
          <DocChip status="ok" icon="check" iconSize={12} label={t("manifest.docsOk")} />
        ))}
    </div>
  );

  return (
    <div>
      <div className="manifest-bar">
        <div className="manifest-progress">
          {summary.hasRoleTargets ? (
            // One stacked progress row per role, each with its own Complete + docs badges.
            <div className="mf-roles">
              {roleRow({
                key: "players",
                labelKey: "manifest.playersAssigned",
                assigned: summary.assignedPlayers,
                requested: summary.requestedPlayers ?? 0,
                pct: summary.playerPct,
                complete: summary.playersComplete,
                docIssues: summary.playerDocIssues,
              })}
              {roleRow({
                key: "staff",
                labelKey: "manifest.staffAssigned",
                assigned: summary.assignedStaff,
                requested: summary.requestedStaff ?? 0,
                pct: summary.staffPct,
                complete: summary.staffComplete,
                docIssues: summary.staffDocIssues,
              })}
            </div>
          ) : (
            <>
              {/* Legacy/sparse trips with no requested breakdown: overall progress + roster split. */}
              <span className="mf-sub">
                <span>
                  <b>
                    {summary.assignedCount} / {trip.paxCount ?? "—"}
                  </b>{" "}
                  {t("manifest.assigned")}
                </span>
              </span>
              <ProgressBar
                value={summary.pct}
                variant={summary.complete ? "success" : "primary"}
              />
              <span className="mf-sub">
                <span>
                  <b>{summary.assignedPlayers}</b>/{summary.totalPlayers} {t("manifest.players")}
                </span>
                <span>
                  <b>{summary.assignedStaff}</b>/{summary.totalStaff} {t("manifest.staff")}
                </span>
              </span>
              {summary.complete && (
                <DocChip status="ok" icon="check" iconSize={12} label={t("manifest.complete")} />
              )}
              {summary.docIssues > 0 ? (
                <DocChip
                  status="warn"
                  icon="alert"
                  iconSize={12}
                  label={`${summary.docIssues} ${t("manifest.docsAttention")}`}
                />
              ) : summary.assignedCount > 0 ? (
                <DocChip status="ok" icon="check" iconSize={12} label={t("manifest.docsOk")} />
              ) : null}
            </>
          )}
        </div>
        <div className={`mf-submit${locked ? " mf-submit-locked" : ""}`}>
          {locked ? (
            // Closed-out record: a Locked badge replaces the submit control; exports stay on hand.
            <>
              <DocChip status="ok" icon="lock" iconSize={12} label={t("manifest.locked")} />
              {exportButtons}
            </>
          ) : (
            <>
              {!isBackOffice && (
                <Button
                  icon="send"
                  // With a requested breakdown, require every role filled (both bars at 100%);
                  // legacy trips just need at least one passenger assigned.
                  disabled={
                    (summary.hasRoleTargets ? !summary.complete : summary.assignedCount === 0) ||
                    submitting ||
                    busy
                  }
                  onClick={submit}
                >
                  {submitting ? t("manifest.submitting") : t("manifest.submit")}
                </Button>
              )}
              <span
                className={`mf-submit-status${
                  !submittedAt ? "" : dirty ? " is-modified" : " is-submitted"
                }`}
              >
                {!submittedAt
                  ? t("manifest.notSubmitted")
                  : dirty
                    ? t("manifest.modifiedSince", { date: fmt.dateTime(submittedAt) })
                    : t("manifest.lastSubmitted", { date: fmt.dateTime(submittedAt) })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* The role filter + edit controls only make sense while the list is editable. */}
      {!locked && (
        <div className="mf-controls">
          <Segmented
            size="sm"
            value={tab}
            onChange={setTab}
            options={[
              { value: "all", label: t("roster.viewAll") },
              { value: "player", label: t("roster.player") },
              { value: "staff", label: t("roster.staff") },
              { value: "assigned", label: t("manifest.onList") },
            ]}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {summary.assignedCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll} disabled={busy}>
                {t("manifest.clear")}
              </Button>
            )}
            {exportButtons}
            <div style={{ maxWidth: 220, width: "100%" }}>
              <TextInput value={q} onChange={setQ} placeholder={t("roster.search")} icon="search" />
            </div>
          </div>
        </div>
      )}

      <Card pad={false}>
        <table className="table roster-table">
          <thead>
            <tr>
              <th style={{ width: 30 }} aria-label="assigned" />
              <th>{t("roster.name")}</th>
              <th>{t("roster.role")}</th>
              <th>{t("roster.nationality")}</th>
              <th>{t("roster.docType")}</th>
              <th>{t("roster.expiry")}</th>
              <th>{t("roster.docStatus")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="mf-empty">
                  {t("manifest.empty")}
                </td>
              </tr>
            ) : (
              rows.map((p) => {
                const on = assignedIds.has(p.id);
                const blocked = atCapacity(p);
                const readiness = READINESS[p.documentStatus];
                return (
                  <tr
                    key={p.id}
                    className={`mf-row${on ? " on" : ""}${blocked ? " mf-row-blocked" : ""}${locked ? " mf-row-locked" : ""}`}
                    onClick={locked ? undefined : () => !busy && toggle(p)}
                  >
                    <td>
                      <span className="mf-check">{on && <Icon name="check" size={13} />}</span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar initials={initials(p.fullName)} size={30} />
                        <b style={{ fontWeight: 600 }}>{p.fullName}</b>
                      </div>
                    </td>
                    <td>
                      {p.isStaff ? (
                        <span className="tag">{t("roster.staff")}</span>
                      ) : (
                        <span className="flag">{t("roster.player")}</span>
                      )}
                    </td>
                    <td className="mono muted">{p.nationality ?? "—"}</td>
                    <td>{p.documentType ?? "—"}</td>
                    <td className="mono muted" style={{ fontSize: 13 }}>
                      {p.documentExpiry ? fmt.date(p.documentExpiry) : "—"}
                    </td>
                    <td>
                      <DocChip status={readiness.chip} icon={readiness.icon} iconSize={12} label={t(readiness.key)} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>

      {toast && <Toast message={toast} />}
    </div>
  );
}

/** Up-to-two-letter initials for the avatar (mirrors the roster table). */
function initials(name: string): string {
  return name
    .split(" ")
    .map((x) => x[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
